import { useEffect, useRef } from 'react';
import { websocketService } from '../services/websocketService';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';

export const useWebSocket = () => {
  const { user, token } = useAuth();
  const { organization } = useOrganization();
  const isConnected = useRef(false);

  useEffect(() => {
    if (token && user && organization && !isConnected.current) {
      // Connect to WebSocket
      websocketService.connect(token);
      websocketService.joinOrganization(organization.id);
      isConnected.current = true;

      // Cleanup on unmount
      return () => {
        if (organization) {
          websocketService.leaveOrganization(organization.id);
        }
        websocketService.disconnect();
        isConnected.current = false;
      };
    }
  }, [token, user, organization]);

  return {
    isConnected: websocketService.isConnected(),
    subscribe: websocketService.on.bind(websocketService),
    unsubscribe: websocketService.off.bind(websocketService),
    send: websocketService.send.bind(websocketService),
  };
};

// Hook específico para logs de acceso en tiempo real
export const useRealTimeAccessLogs = (callback: (log: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToAccessLogs(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para estado de Hubs (online/offline en tiempo real)
export const useRealTimeHubStatus = (callback: (data: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToHubStatus(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para estado de dispositivos (online/offline reportado por Hub)
export const useRealTimeDeviceStatus = (callback: (data: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToDeviceStatus(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para estado de controladoras
export const useRealTimeControllerStatus = (callback: (data: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToControllerStatus(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para notificaciones
export const useRealTimeNotifications = (callback: (notification: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToNotifications(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para alertas
export const useRealTimeAlerts = (callback: (alert: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToAlerts(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para telemetría de Hub (métricas de hardware en tiempo real)
export const useRealTimeHubTelemetry = (callback: (data: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToHubTelemetry(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para cambios de configuración de dispositivos (relay mode)
export const useRealTimeDeviceConfig = (callback: (data: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToDeviceConfigUpdate(callback);
    return unsubscribe;
  }, [callback]);
};

// Hook para actualizaciones de comandos de Hub (reboot, restart acks)
export const useRealTimeHubCommands = (callback: (data: any) => void) => {
  useEffect(() => {
    const unsubscribe = websocketService.subscribeToHubCommandUpdate(callback);
    return unsubscribe;
  }, [callback]);
};
